/**
 * The settings screen: controls (sensitivity, key bindings), view, graphics,
 * audio, comfort and data. Every change produces a new Settings object
 * (validated by sanitizeSettings) and is applied and saved by the app at once.
 *
 * Rebinding: press "Change", then the new key (Esc cancels). A key already
 * used by another action is refused with the name of that action, so the
 * table never contains a conflict; RESERVED_KEYS are refused with a reason.
 */
import { button, h } from '../editor/dom';
import { ACTION_LABELS, DEFAULT_SETTINGS, MAX_KEYS_PER_ACTION, OUTLINE_PRESETS, RANGES, REBINDABLE, RESERVED_KEYS, bindingConflicts, colourHex, gameBindings, keyLabel, outlineColourProblem, sanitizeSettings, type RebindableAction, type Settings } from '../ui/Settings';

export interface SettingsPanelHost {
  settings(): Readonly<Settings>;
  change(next: Settings): void;
  /** Clears local records after confirmation. */
  resetProgress(): void;
  /** Interface sound for a button press. */
  click(): void;
}

type Section = 'controls' | 'video' | 'audio' | 'comfort' | 'ghost' | 'data';
const SECTIONS: readonly [Section, string][] = [
  ['controls', 'Controls'],
  ['video', 'Video'],
  ['audio', 'Audio'],
  ['comfort', 'Comfort'],
  ['data', 'Data'],
];

export class SettingsPanel {
  readonly root = h('div', { className: 'settings-panel' });
  private section: Section = 'controls';
  private capture: { action: RebindableAction; slot: number } | null = null;
  private readonly notice = h('p', { className: 'settings-notice', role: 'status' });
  private readonly body = h('div', { className: 'settings-body' });
  private readonly nav = h('nav', { className: 'settings-nav', ariaLabel: 'Settings sections' });
  private readonly abort = new AbortController();

  constructor(private readonly host: SettingsPanelHost) {
    this.root.append(this.nav, this.body, this.notice);
    // Capture phase, so a key pressed while rebinding never reaches the game or menus.
    document.addEventListener('keydown', (e) => this.onKey(e), { capture: true, signal: this.abort.signal });
    this.render();
  }

  dispose(): void {
    this.abort.abort();
    this.root.remove();
  }

  /** True while waiting for a key (the app must not treat Esc as "back"). */
  get capturing(): boolean {
    return this.capture !== null;
  }

  render(): void {
    this.nav.replaceChildren(
      ...SECTIONS.map(([id, label]) =>
        button(label, () => {
          this.host.click();
          this.section = id;
          this.capture = null;
          this.render();
        }, { className: id === this.section ? 'active' : '', dataset: { section: id } }),
      ),
    );
    const s = this.host.settings();
    const parts: Record<Section, () => HTMLElement[]> = {
      controls: () => [this.slider('Mouse sensitivity', 'sensitivity', RANGES.sensitivity, 0.05, (v) => v.toFixed(2)), this.bindingsTable(s)],
      video: () => [
        this.outlineColour(s),
        this.slider('Field of view', 'fovDeg', RANGES.fovDeg, 1, (v) => `${v.toFixed(0)}°`),
        this.choice('Graphics quality', 'graphics', [['low', 'Low'], ['medium', 'Medium'], ['high', 'High']], 'Low: no antialiasing. Medium: antialiased outlines. High: antialiased, full resolution on high-density screens. Antialiasing applies when a track next loads.'),
        this.slider('Render scale', 'renderScale', RANGES.renderScale, 0.05, (v) => `${Math.round(v * 100)}%`),
        this.toggle('Camera roll', 'roll', 'A slight tilt into sideways motion. Off by default; always off with reduced motion.'),
      ],
      audio: () => [
        this.slider('Master volume', 'masterVolume', RANGES.volume, 0.05, pct),
        this.slider('Effects volume', 'effectsVolume', RANGES.volume, 0.05, pct),
        this.slider('Music volume', 'musicVolume', RANGES.volume, 0.05, pct, 'No music ships with this build yet; the setting is kept for when it does.'),
        this.toggle('Speed sound', 'speedSound', 'Rushing air that rises with speed and near walls.'),
      ],
      comfort: () => [
        this.toggle('Reduced motion', 'reducedMotion', 'No interface animation and no camera roll.'),
        this.choice('Screen flashes', 'flashes', [['full', 'Full'], ['reduced', 'Reduced'], ['off', 'Off']], 'The short colour flash on death, checkpoints and finishing.'),
        this.slider('Interface scale', 'uiScale', RANGES.uiScale, 0.05, pct),
        this.toggle('High contrast', 'highContrast', 'Every outline at full strength, and brighter interface text.'),
        this.choice('HUD', 'hud', [['minimal', 'Minimal'], ['expanded', 'Expanded']], 'Expanded adds vertical speed, ground contact, surface, attempt and deaths.'),
      ],
      ghost: () => [
        this.choice('Ghost', 'ghost', [['pb', 'Personal best'], ['last', 'Previous attempt'], ['off', 'Off']], 'A see-through runner replaying a recorded run beside you. It never collides, triggers anything or affects your time.'),
        this.slider('Ghost opacity', 'ghostOpacity', RANGES.ghostOpacity, 0.05, pct),
        this.slider('Ghost start offset', 'ghostOffset', RANGES.ghostOffset, 0.25, (v) => `${v > 0 ? '+' : ''}${v.toFixed(2)} s`, 'Positive: the ghost is that far ahead of the run. Negative: it waits that long after you start.'),
      ],
      data: () => [this.dataSection()],
    };
    this.body.replaceChildren(...parts[this.section]());
  }

  // ---------------------------------------------------------------- controls

  /** Presets and a custom colour for the outlines (game and interface). Hazard colours are refused. */
  private outlineColour(s: Readonly<Settings>): HTMLElement {
    const set = (color: number): void => {
      const problem = outlineColourProblem(color);
      if (problem) {
        this.say(problem);
        this.render();
        return;
      }
      this.host.click();
      this.host.change(sanitizeSettings({ ...this.host.settings(), outlineColor: color }));
      this.say('');
      this.render();
    };
    const presets = OUTLINE_PRESETS.map((p) => {
      const b = button('', () => set(p.color), { className: `swatch${p.color === s.outlineColor ? ' active' : ''}`, title: p.name, dataset: { outline: colourHex(p.color) } });
      b.style.setProperty('--swatch', colourHex(p.color));
      b.setAttribute('aria-label', p.name);
      b.setAttribute('aria-pressed', String(p.color === s.outlineColor));
      return b;
    });
    const custom = h('input', { type: 'color', value: colourHex(s.outlineColor), title: 'Custom colour' });
    custom.dataset['setting'] = 'outlineColor';
    custom.addEventListener('change', () => set(Number.parseInt(custom.value.slice(1), 16)));
    return h(
      'div',
      { className: 'setting setting-outline' },
      h('span', { className: 'setting-name', textContent: 'Outline colour' }),
      h('div', { className: 'swatch-row' }, ...presets, h('label', { className: 'swatch custom', title: 'Custom colour' }, custom, h('span', { textContent: 'Custom' }))),
      h('small', { textContent: 'Safe edges in the game and every line in the interface. Lethal edges stay red and reactive edges cyan, so colours close to those are refused.' }),
    );
  }

  private slider(label: string, key: 'sensitivity' | 'fovDeg' | 'renderScale' | 'masterVolume' | 'musicVolume' | 'effectsVolume' | 'uiScale' | 'ghostOpacity' | 'ghostOffset', range: readonly [number, number], step: number, show: (v: number) => string, help?: string): HTMLElement {
    const s = this.host.settings();
    const out = h('output', { textContent: show(s[key]) });
    const input = h('input', { type: 'range', min: String(range[0]), max: String(range[1]), step: String(step), value: String(s[key]) });
    input.dataset['setting'] = key;
    input.addEventListener('input', () => {
      const v = Number(input.value);
      out.textContent = show(v);
      this.host.change(sanitizeSettings({ ...this.host.settings(), [key]: v }));
    });
    return h('label', { className: 'setting' }, h('span', { className: 'setting-name', textContent: label }), input, out, help ? h('small', { textContent: help }) : null);
  }

  private toggle(label: string, key: 'roll' | 'speedSound' | 'reducedMotion' | 'highContrast', help: string): HTMLElement {
    const input = h('input', { type: 'checkbox', checked: this.host.settings()[key] });
    input.dataset['setting'] = key;
    input.addEventListener('change', () => this.host.change(sanitizeSettings({ ...this.host.settings(), [key]: input.checked })));
    return h('label', { className: 'setting setting-toggle' }, input, h('span', { className: 'setting-name', textContent: label }), h('small', { textContent: help }));
  }

  private choice<K extends 'graphics' | 'flashes' | 'hud' | 'ghost'>(label: string, key: K, options: readonly [Settings[K], string][], help: string): HTMLElement {
    const select = h('select', {});
    select.dataset['setting'] = key;
    for (const [value, text] of options) select.append(h('option', { value, textContent: text, selected: this.host.settings()[key] === value }));
    select.addEventListener('change', () => this.host.change(sanitizeSettings({ ...this.host.settings(), [key]: select.value })));
    return h('label', { className: 'setting' }, h('span', { className: 'setting-name', textContent: label }), select, h('small', { textContent: help }));
  }

  private bindingsTable(s: Readonly<Settings>): HTMLElement {
    const table = gameBindings(s.bindings);
    const rows = REBINDABLE.map((action) => {
      const keys = table[action];
      const slots = Array.from({ length: MAX_KEYS_PER_ACTION }, (_, slot) => {
        const waiting = this.capture?.action === action && this.capture.slot === slot;
        const code = keys[slot];
        return button(waiting ? 'Press a key…' : code ? keyLabel(code) : '—', () => {
          this.host.click();
          this.capture = { action: action as RebindableAction, slot };
          this.say(`Press the new key for ${ACTION_LABELS[action]}. Esc cancels.`);
          this.render();
        }, { className: waiting ? 'key waiting' : 'key', dataset: { bind: `${action}:${slot}` } });
      });
      const clear = keys.length > 1 ? button('×', () => this.setKeys(action as RebindableAction, keys.slice(0, 1)), { className: 'key-clear', title: 'Remove the second key' }) : null;
      return h('div', { className: 'binding' }, h('span', { textContent: ACTION_LABELS[action] }), ...slots, clear);
    });
    const reset = button('Reset keys to defaults', () => {
      this.host.click();
      this.host.change(sanitizeSettings({ ...this.host.settings(), bindings: {} }));
      this.say('Keys reset to the defaults.');
      this.render();
    }, { className: 'secondary' });
    return h('section', { className: 'bindings' }, h('h3', { textContent: 'Keys' }), h('p', { className: 'setting-help', textContent: 'Esc always pauses. F3 shows diagnostics.' }), ...rows, reset);
  }

  private onKey(e: KeyboardEvent): void {
    const c = this.capture;
    if (!c) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (e.code === 'Escape') {
      this.capture = null;
      this.say('Rebinding cancelled.');
      this.render();
      return;
    }
    const result = this.tryBind(c.action, c.slot, e.code);
    this.say(result);
  }

  /** Applies one key to one slot; returns the message for the player. Exposed for tests through the DOM only. */
  private tryBind(action: RebindableAction, slot: number, code: string): string {
    if (RESERVED_KEYS.has(code)) return `${keyLabel(code)} can't be bound (it is reserved for pausing, developer keys or the browser). Press another key, or Esc to cancel.`;
    const table = gameBindings(this.host.settings().bindings);
    const owner = REBINDABLE.find((a) => a !== action && table[a].includes(code));
    if (owner) return `${keyLabel(code)} is already used by ${ACTION_LABELS[owner]}. Change that action first, or press another key (Esc cancels).`;
    const keys = [...table[action]];
    keys[slot] = code;
    const next = [...new Set(keys.filter(Boolean))];
    this.capture = null;
    const msg = this.setKeys(action, next);
    this.render();
    return msg;
  }

  private setKeys(action: RebindableAction, keys: string[]): string {
    const s = this.host.settings();
    const next = sanitizeSettings({ ...s, bindings: { ...s.bindings, [action]: keys } });
    const conflicts = bindingConflicts(gameBindings({ ...s.bindings, [action]: keys }));
    if (conflicts.length || (next.bindings[action]?.join() ?? '') !== keys.join()) {
      return `That key table would conflict (${conflicts.map((c) => keyLabel(c.code)).join(', ')}); nothing changed.`;
    }
    this.host.change(next);
    this.render();
    return `${ACTION_LABELS[action]}: ${keys.map(keyLabel).join(' or ')}.`;
  }

  // ---------------------------------------------------------------- data

  private dataSection(): HTMLElement {
    const confirm = h('div', { className: 'confirm', hidden: true });
    const ask = button('Reset all local records…', () => {
      this.host.click();
      confirm.hidden = false;
      ask.hidden = true;
    }, { className: 'danger' });
    confirm.append(
      h('p', { textContent: 'Delete every personal best, split and completion stored in this browser? Your tracks in the editor are not affected. This cannot be undone.' }),
      button('Delete records', () => {
        this.host.resetProgress();
        confirm.hidden = true;
        ask.hidden = false;
        this.say('Local records deleted.');
      }, { className: 'danger', dataset: { confirm: 'reset-progress' } }),
      button('Keep them', () => {
        confirm.hidden = true;
        ask.hidden = false;
      }, { className: 'secondary' }),
    );
    const resetAll = button('Restore default settings', () => {
      this.host.click();
      this.host.change({ ...DEFAULT_SETTINGS, bindings: {} });
      this.say('Default settings restored.');
      this.render();
    }, { className: 'secondary' });
    return h('section', { className: 'data' }, h('p', { className: 'setting-help', textContent: 'Settings, records and tracks are stored only in this browser. Nothing is sent anywhere.' }), resetAll, ask, confirm);
  }

  private say(text: string): void {
    this.notice.textContent = text;
  }
}

function pct(v: number): string {
  return `${Math.round(v * 100)}%`;
}
